import React from "react";
import { useMonyawn } from "../../../context/MonyawnContext";
import { DEFAULT_INTEGRATION_HEALTH } from "../../../integrations/contracts";
import { FLAGS } from "../../../lib/flags";
import { requestGoogleWorkspaceConnect } from "../../../integrations/googleWorkspace";
import { gmailAdapter } from "../../../integrations/gmail";
import { googleCalendarAdapter } from "../../../integrations/googleCalendar";

export const IntegrationsPanel: React.FC = () => {
  const { state, patchState, selectedOpportunity, setNotice } = useMonyawn();

  const integrations =
    state.integrationHealthSnapshot.length > 0
      ? state.integrationHealthSnapshot
      : DEFAULT_INTEGRATION_HEALTH;

  const markIntegration = (id: string, status: "connected" | "degraded" | "disconnected") => {
    patchState(
      (s) => ({
        ...s,
        integrationHealthSnapshot: integrations.map((i) =>
          i.id === id ? { ...i, status, lastSyncAt: new Date().toISOString() } : i,
        ),
      }),
      `${id} integration ${status}.`,
    );
  };

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const url = new URL(window.location.href);
    const provider = url.searchParams.get("integration_provider");
    const status = url.searchParams.get("integration_status");

    if (!provider || !status) {
      return;
    }

    if (status === "connected") {
      markIntegration(provider, "connected");
      setNotice({
        tone: "success",
        message: `${provider} connected successfully.`,
      });
    } else {
      markIntegration(provider, "degraded");
      setNotice({
        tone: "info",
        message: `${provider} connect flow returned status: ${status}.`,
      });
    }

    url.searchParams.delete("integration_provider");
    url.searchParams.delete("integration_status");
    window.history.replaceState({}, "", url.toString());
  }, []);

  const handleConnect = async (id: string) => {
    if (id === "gmail" && !FLAGS.gmail_enabled) {
      setNotice({
        tone: "info",
        message: "Gmail integration is currently gated. Set VITE_FLAG_GMAIL=true to enable.",
      });
      return;
    }

    if (id === "google_calendar" && !FLAGS.google_calendar_enabled) {
      setNotice({
        tone: "info",
        message:
          "Google Calendar integration is currently gated. Set VITE_FLAG_GOOGLE_CALENDAR=true to enable.",
      });
      return;
    }

    if (id === "linkedin" && !FLAGS.linkedin_enabled) {
      setNotice({
        tone: "info",
        message: "LinkedIn integration is currently gated. Set VITE_FLAG_LINKEDIN=true to enable.",
      });
      return;
    }

    if (id === "gmail" || id === "google_calendar") {
      if (!state.selectedUserId) {
        setNotice({
          tone: "info",
          message: "Select an active user context before connecting integrations.",
        });
        return;
      }

      const connect = await requestGoogleWorkspaceConnect(id, {
        userId: state.selectedUserId,
        accountId: state.selectedAccountId,
      });
      if (connect.authorizationUrl) {
        window.location.href = connect.authorizationUrl;
        return;
      }

      if (connect.connected) {
        markIntegration(id, "connected");
        setNotice({
          tone: "success",
          message: `${id} connected. Monyawn is now listening for signals.`,
        });
        return;
      }

      markIntegration(id, "degraded");
      setNotice({
        tone: "info",
        message:
          connect.reason ??
          `${id} needs backend OAuth setup. Configure Supabase functions for Google Workspace connect.`,
      });
      return;
    }

    // LinkedIn remains a partner-gated connector until official API access is approved.
    markIntegration(id, "degraded");
    setNotice({
      tone: "info",
      message: `${id} is partner-gated. Keep using signal intake while OAuth access is prepared.`,
    });
  };

  const handleSync = async (id: string) => {
    if (!selectedOpportunity) {
      setNotice({
        tone: "info",
        message: "Select an active opportunity before syncing integration signals.",
      });
      return;
    }

    if (id !== "gmail" && id !== "google_calendar") {
      setNotice({
        tone: "info",
        message: `${id} sync is not available yet.`,
      });
      return;
    }

    const adapter = id === "gmail" ? gmailAdapter : googleCalendarAdapter;
    const signals = await adapter.pullSignals(selectedOpportunity.opportunity_id);

    markIntegration(id, signals.length > 0 ? "connected" : "degraded");
    setNotice({
      tone: "success",
      message: `${id} sync completed with ${signals.length} signal${signals.length === 1 ? "" : "s"}.`,
    });
  };

  return (
    <div className="flex flex-col gap-6 p-8 rounded-[2rem] bg-white/40 border border-black/5 shadow-brand-shadow">
      <div>
        <h3 className="text-xl font-bold text-brand-ink mb-1">Integration Hub 🛰️</h3>
        <p className="text-sm text-brand-muted">
          Connect your tools to move from manual entry to autonomous plays.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {integrations.map((integration) => (
          <div
            key={integration.id}
            className="p-5 rounded-2xl border border-black/5 bg-white/60 flex flex-col gap-4"
          >
            <div className="flex justify-between items-start">
              <div>
                <p className="font-bold text-brand-ink">{integration.label}</p>
                <div className="flex items-center gap-1.5 mt-1">
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      integration.status === "connected"
                        ? "bg-green-500"
                        : integration.status === "degraded"
                          ? "bg-orange-500"
                          : "bg-gray-300"
                    }`}
                  ></span>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-brand-muted">
                    {integration.status}
                  </p>
                </div>
              </div>
              <button
                onClick={() => void handleConnect(integration.id)}
                disabled={integration.status === "connected"}
                className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all ${
                  integration.status === "connected"
                    ? "bg-black/5 text-brand-muted cursor-default"
                    : "bg-brand-accent text-white hover:bg-brand-accent-strong active:scale-95"
                }`}
              >
                {integration.status === "connected" ? "Connected" : "Connect"}
              </button>
              <button
                onClick={() => void handleSync(integration.id)}
                className="px-4 py-1.5 rounded-full text-xs font-bold transition-all bg-black/5 text-brand-ink hover:bg-black/10 active:scale-95"
              >
                Sync now
              </button>
            </div>

            <div className="flex flex-wrap gap-2">
              {integration.capabilities.map((cap) => (
                <span
                  key={cap}
                  className="px-2 py-0.5 rounded-full bg-black/5 text-[9px] font-bold text-brand-muted border border-black/5"
                >
                  {cap.replace(/_/g, " ")}
                </span>
              ))}
            </div>

            {integration.lastSyncAt && (
              <p className="text-[10px] text-brand-muted italic mt-auto">
                Last sync: {new Date(integration.lastSyncAt).toLocaleTimeString()}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
